import { SubmissionErrors } from 'final-form';
import { FC, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import i18next from 'i18next';

import { Question } from '~src/interfaces/question.interface';
import { StoreI } from '~src/rootReducer';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';
import DragList from '~src/shared/lists/dragList/DragList';
import SvgIcon from '~src/shared/SvgIcon';
import { getDefaultSurveyInvitation, Survey } from '~src/modules/surveys/interfaces';
import { TopicValueI } from '~src/modules/surveys/components/topic_select/edit/TopicQuestionsForm';

import SummaryQuestionItem from './SummaryQuestionItem';
import * as SC from './summarySelect.sc';

interface OwnProps {
  survey: Survey | null;
  saveSurvey: (request: TopicValueI) => Promise<SubmissionErrors | void>;
  t: i18next.TFunction;
}

interface StateProps {
  allQuestions: { [key: string]: Question };
}

type Props = StateProps & OwnProps & RouteComponentProps<any>;

const SummarySelect: FC<Props> = props => {
  const [questionList, setQuestionList] = useState<Question[]>([]);

  useEffect(() => {
    if (props.survey && props.survey.questions) {
      setQuestionList(props.survey.questions.map(q => props.allQuestions[q.question_id]));
    }
  }, []);

  function renderListItem(item: Question) {
    return <SummaryQuestionItem question={item} />;
  }

  function onListChange(newList: Question[]) {
    if (props.survey) {
      const questionIds = newList.map(item => item.id);
      setQuestionList(newList);
      props.saveSurvey({
        surveyId: props.survey.id,
        body: {
          name: props.survey.name,
          questions: questionIds.map(x => ({ question_id: x, mandatory: false })),
          invitation: getDefaultSurveyInvitation(props.t),
        },
      });
    }
  }

  function goToEdit() {
    props.history.replace(`/content/surveys/${props.survey ? props.survey.id : ''}/edit`);
  }

  return (
    <SC.SummarySelectContainer>
      <SC.SummaryPre data-translation="x.survey.details.summary.subtitle">
        {props.t('x.survey.details.summary.subtitle')}
      </SC.SummaryPre>
      <SC.SummaryQuestionHeader>
        <SC.SummaryTitle data-translation="x.survey.details.summaray.list.title">
          <span>{`${props.t('x.survey.details.summaray.list.title')} (${questionList.length})`}</span>
          <HoverInfoIcon
            infoText={{
              key: 'x.survey.details.summary.list.hover',
              txt: props.t('x.survey.details.summary.list.hover'),
            }}
          />
        </SC.SummaryTitle>
        <SC.SummaryButton onClick={goToEdit}>
          <SvgIcon width={25} name="iconPen" />
          <span data-translation="x.survey.details.summary.cta.to_edit">
            {props.t('x.survey.details.summary.cta.to_edit')}
          </span>
        </SC.SummaryButton>
      </SC.SummaryQuestionHeader>
      {questionList.length > 0 && (
        <ul>
          <DragList renderTemplate={renderListItem} list={questionList} onListChange={onListChange} />
        </ul>
      )}
    </SC.SummarySelectContainer>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  allQuestions: state.topics.questionsById,
});

const SummarySelectSuperComponent = connect(mapStateToProps)(SummarySelect);
const SummarySelectWithRouter = withRouter(SummarySelectSuperComponent);
export default SummarySelectWithRouter;
